import { Box } from "@mui/material";
import { Canvas, useBoardContext } from "@rastreabilidadebrasil/tageditor";
import ToolsBar from "./toolsBar";
import ActionBar from "./actionBar";
import EtiquetaPaginator from "./tools/etiquetaPaginator";
import { useCallback, useEffect } from "react";
import EtiquetaEditorPages from "./tools/etiquetaEditorPages";
import GlobalHandlers from "./globalHandlers";
import ZoomStatus from "./tools/zoomStatus";
import { useEditorContext } from "../../../../context/editorContext/EditorContext";
import DadosEtiqueta from "./tools/DadosEtiqueta";
import { fabric } from "fabric";
import remove from "../../../../assets/icons/remove.svg";

function TagEditor(props: {
  setOpenModalMinhasEtiquetas: (value: boolean) => void;
  setActiveStep: (step: number) => void;
}) {
  const { etiquetaData } = useEditorContext();
  const { boardFile } = useBoardContext();


  // Encaixa a arte importada na etiqueta. O ajuste precisa ser gravado no documento
  // (boardFile.applyAll) e não só no canvas: ao trocar de etiqueta o editor recarrega
  // os objetos a partir do documento, e um ajuste feito só no canvas era perdido,
  // fazendo os itens "pularem" de posição na primeira troca.
  const applyScaleToFit = useCallback(async () => {
    const board = boardFile as any;
    const canvas = board?.canvas || board?.boardCanvas?.canvas || board?.boardCanvas;
    if (!canvas?.getObjects || typeof board?.applyAll !== "function") return;

    const isWorkspace = (obj: any) =>
      obj.id === "workspace" || obj["x-type"] === "workspace" || !obj.selectable;

    try {
      const allObjects: fabric.Object[] = canvas.getObjects();
      const workspaceObj = allObjects.find(isWorkspace);
      const arteObjects = allObjects.filter((obj) => !isWorkspace(obj));
      if (!workspaceObj || arteObjects.length === 0) return;

      const selecao = new fabric.ActiveSelection(arteObjects, { canvas });
      const origemCentro = selecao.getCenterPoint();
      const largura = selecao.width || 1;
      const altura = selecao.height || 1;
      selecao.destroy();

      const padding = 5;
      const workspaceWidth = workspaceObj.width! * (workspaceObj.scaleX || 1);
      const workspaceHeight = workspaceObj.height! * (workspaceObj.scaleY || 1);
      const escala = Math.min(
        (workspaceWidth - padding * 2) / largura,
        (workspaceHeight - padding * 2) / altura,
        1,
      );
      const destinoCentro = workspaceObj.getCenterPoint();

      const semMudanca =
        escala === 1 &&
        Math.abs(origemCentro.x - destinoCentro.x) < 0.5 &&
        Math.abs(origemCentro.y - destinoCentro.y) < 0.5;
      if (semMudanca) return;

      await board.applyAll((obj: fabric.Object) => {
        if (isWorkspace(obj)) return;
        const centro = obj.getCenterPoint();
        const novoCentro = new fabric.Point(
          destinoCentro.x + (centro.x - origemCentro.x) * escala,
          destinoCentro.y + (centro.y - origemCentro.y) * escala,
        );
        obj.set({
          scaleX: (obj.scaleX || 1) * escala,
          scaleY: (obj.scaleY || 1) * escala,
        });
        obj.setPositionByOrigin(novoCentro, "center", "center");
        obj.setCoords();
      });
    } catch (error) {
      console.error("Error applying Scale to Fit in TagEditor:", error);
    }
  }, [boardFile]);

  const getCanvas = () => {
    const boardForCanvas = boardFile as any;

    return (
      boardForCanvas?.canvas || boardForCanvas?.boardCanvas || boardForCanvas?.boardCanvas?.canvas
    );
  };

  const createDeleteControl = (img: HTMLImageElement) => {
    return new fabric.Control({
      x: 0.5,
      y: -0.5,
      offsetY: -16,
      cursorStyle: "pointer",

      mouseUpHandler: (_eventData, transform) => {
        const target = transform.target;
        const currentCanvas = target.canvas;

        if (currentCanvas) {
          currentCanvas.remove(target);
          currentCanvas.discardActiveObject();
          currentCanvas.requestRenderAll();
        }

        return true;
      },

      render: (ctx, left, top) => {
        const size = 30;

        ctx.save();
        ctx.translate(left, top);
        ctx.drawImage(img, -size / 2, -size / 2, size, size);
        ctx.restore();
      },
    });
  };

  const applyDeleteControl = (canvas: fabric.Canvas, deleteControl: fabric.Control) => {
    canvas.getObjects().forEach((obj: any) => {
      obj.controls.deleteControl = deleteControl;
    });

    canvas.requestRenderAll();
  };

  const loadDeleteIcon = (canvas: fabric.Canvas) => {
    const img = document.createElement("img");
    img.src = remove;

    img.onload = () => {
      const deleteControl = createDeleteControl(img);
      applyDeleteControl(canvas, deleteControl);
    };
  };

  // Ajusta a arte só ao importar um modelo. Rodar isso a cada troca de etiqueta
  // reescalava e recentralizava objetos que o usuário já tinha posicionado.
  useEffect(() => {
    if (etiquetaData) {
      boardFile
        ?.importTemplate(etiquetaData)
        .then(() => applyScaleToFit())
        .catch((error) => {
          alert(error);
        });
    }
  }, [boardFile, etiquetaData]);

  useEffect(() => {
    const handleTabClose = (event: any) => {
      event.preventDefault();
      return (event.returnValue = "");
    };

    window.addEventListener("beforeunload", handleTabClose);

    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, []);

  useEffect(() => {
    const canvas = getCanvas();

    if (!canvas) {
      return;
    }

    loadDeleteIcon(canvas);
  }, [boardFile]);

  return (
    <>
      <GlobalHandlers />

      <ActionBar
        setOpenModalMinhasEtiquetas={props.setOpenModalMinhasEtiquetas}
        setActiveStep={props.setActiveStep}
      />

      <Box className="editor-container">
        <ToolsBar />

        <EtiquetaPaginator />

        <Canvas />

        <EtiquetaEditorPages />

        <DadosEtiqueta />

        <ZoomStatus />
      </Box>
    </>
  );
}

export default TagEditor;
